import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useColorScheme,
} from '@mui/material';
import QuoteForm from '../../components/QuoteForm';
import {
  HeroCheckBullet,
  TrustpilotHeroStars,
  TrustpilotHeroWordmark,
  TrustpilotHeroWordmarkDark,
} from '../../components/icons';
import HomeUsps from '../../components/HomeUsps/HomeUsps';
import { ANALYTICS, ENV, STRINGS } from '../../constants';
import { QUOTES } from '../../constants/routes';
import {
  HOME_LANDING_SECTIONS,
  HOME_TRUST_BADGE_MSE,
} from '../../constants/homeLanding';
import ConfigSlice from '../../redux/configSlice';
import useBreakpoint from '../../hooks/useBreakpoint';
import useAnalytics from '../../hooks/useAnalytics';
import { formatIndividualPrice } from '../../utils/strings';

const homeLandingImage = name => `${ENV.CDN_BASE_URL_IMAGES}/${name}`;

const heroBackgroundImage = homeLandingImage('hero.png');

const SECTION_IMAGES = [
  homeLandingImage('section-01-send-parcels.png'),
  homeLandingImage('section-02-worldwide.png'),
  homeLandingImage('section-03-lockers.png'),
  homeLandingImage('section-04-app.png'),
  homeLandingImage('section-05-london.png'),
  homeLandingImage('section-06-pro.png'),
  homeLandingImage('section-07-delivered.png'),
];

function TrustBadgesOnImage() {
  const { colorScheme } = useColorScheme();
  const isDark = colorScheme === 'dark';

  return (
    <Box
      sx={{
        position: 'absolute',
        left: { xs: 14, sm: 44, md: 56 },
        top: { xs: 6, sm: 20, md: 24 },
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 0.5, sm: 1, md: 2 },
        maxWidth: { xs: 150, sm: 180, md: 200 },
        pointerEvents: 'none',
      }}
    >
      <Paper
        elevation={1}
        sx={{ borderRadius: 1, backgroundColor: 'background.paper' }}
      >
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
          <Typography
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              fontSize: { xs: 12, sm: 13, md: 14 },
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            {HOME_TRUST_BADGE_MSE.brand}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
            <Box
              sx={{
                width: 39,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'primary.pageBackground',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: { xs: 16, sm: 17, md: 18 },
                color: 'text.primary',
                flexShrink: 0,
              }}
            >
              <img
                src={
                  isDark
                    ? homeLandingImage('section-01-nineDark.png')
                    : homeLandingImage('section-01-nine.png')
                }
                alt='9'
                style={{ width: '100%', height: '100%' }}
              />
            </Box>
            <Typography sx={{ fontSize: { xs: 12, sm: 13, md: 14 } }}>
              {HOME_TRUST_BADGE_MSE.bodyLine1}
              <br />
              {HOME_TRUST_BADGE_MSE.bodyLine2}
            </Typography>
          </Box>
        </Box>
      </Paper>
      <Paper
        elevation={1}
        sx={{ borderRadius: 1, backgroundColor: 'background.paper' }}
      >
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1,
              mb: 1,
            }}
          >
            {isDark ? (
              <Box
                component='img'
                src={homeLandingImage('trustpilotDark.png')}
                alt='Trustpilot'
                sx={{ height: { xs: 22, md: 26 }, width: { xs: 88, md: 104 } }}
              />
            ) : (
              <TrustpilotHeroWordmarkDark
                sx={{
                  height: { xs: 22, md: 26 },
                  width: { xs: 88, md: 104 },
                }}
              />
            )}
            <Box
              sx={{
                width: { xs: 120, md: 144 },
                height: { xs: 22, md: 28 },
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: { xs: 'scale(1.57)', md: 'scale(2)' },
                  transformOrigin: 'top left',
                }}
              >
                <TrustpilotHeroStars
                  sx={{ width: 72, height: 14, display: 'block' }}
                />
              </Box>
            </Box>
            <Typography
              component='span'
              sx={{
                fontWeight: 700,
                fontSize: { xs: 16, sm: 17, md: 18 },
                lineHeight: 1,
                letterSpacing: 0.15,
                color: 'text.primary',
              }}
            >
              {STRINGS.TRUSTPILOT_EXCELLENT}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: { xs: 10, sm: 11, md: 12 },
              lineHeight: 1.33,
              letterSpacing: 0.15,
              color: 'text.primary',
            }}
          >
            {STRINGS.TRUSTPILOT_REVIEWS}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

function Home() {
  const { setInterfaceId, Trackable } = useAnalytics();
  const { minDomestic, minDomesticWithVatRate } = useSelector(
    ConfigSlice.selectors.getConfigPrices
  );
  const isMobile = useBreakpoint('md');
  const priceWithVatRateText = formatIndividualPrice(minDomesticWithVatRate);
  const priceWithoutVatRateText = formatIndividualPrice(minDomestic);

  useEffect(() => {
    setInterfaceId(ANALYTICS.HOME.INTERFACE_ID);
  }, [setInterfaceId]);

  const formWidth = 960;

  const sendParcelSection = (
    <Box
      sx={{
        position: 'absolute',
        bottom: { md: 11 },
        left: '50%',
        transform: 'translateX(-50%)',
        width: formWidth,
        maxWidth: 'calc(100% - 32px)',
        display: 'flex',
        zIndex: 2,
      }}
    >
      <QuoteForm
        priceWithVatRateText={priceWithVatRateText}
        priceWithoutVatRateText={priceWithoutVatRateText}
      />
    </Box>
  );

  const heroContent = (
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: { xs: 3, md: 6 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          maxWidth: 380,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontWeight: 800,
            fontSize: { md: 48 },
            lineHeight: 1.12,
            color: 'common.white',
          }}
        >
          Delivery
          <br />
          {STRINGS.HERO_HEADLINE.replace(/^Delivery\s*/i, '')}
        </Typography>
        <List dense disablePadding sx={{ listStyle: 'none' }}>
          {STRINGS.HERO_BENEFITS.map(label => (
            <ListItem
              key={label}
              disablePadding
              sx={{
                alignItems: 'center',
                py: 0.75,
                pl: 0,
                pr: 0,
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, mr: 1.25 }}>
                <HeroCheckBullet size={24} />
              </ListItemIcon>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: { xs: 16, md: 16 },
                  lineHeight: 1.5,
                  letterSpacing: 0.15,
                  color: 'common.white',
                }}
              >
                {label}
              </Typography>
            </ListItem>
          ))}
        </List>
        <Box sx={{ pt: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: { xs: 1.5, md: 2.5 },
              mb: 0.5,
            }}
          >
            <TrustpilotHeroWordmark
              sx={{
                height: { xs: 24, md: 28 },
                width: { xs: 64, md: 72 },
              }}
            />
            <TrustpilotHeroStars
              sx={{
                height: { xs: 24, md: 28 },
                width: { xs: 62, md: 72 },
              }}
            />
            <Typography
              component='span'
              sx={{
                fontWeight: 700,
                fontSize: { xs: 16, md: 18 },
                lineHeight: 1,
                letterSpacing: 0.15,
                color: 'common.white',
              }}
            >
              {STRINGS.TRUSTPILOT_EXCELLENT}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: { xs: 11, md: 12 },
              lineHeight: 1.33,
              letterSpacing: 0.15,
              color: 'common.white',
            }}
          >
            {STRINGS.TRUSTPILOT_REVIEWS}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderChecklist = bullets => (
    <List disablePadding sx={{ py: 0 }}>
      {bullets.map(text => (
        <ListItem
          key={text}
          disablePadding
          sx={{ alignItems: 'flex-start', py: 1, px: { xs: 1, md: 2 } }}
        >
          <ListItemIcon sx={{ minWidth: { xs: 44, md: 52 }, mt: 0.25 }}>
            <HeroCheckBullet size={24} />
          </ListItemIcon>
          <ListItemText primary={text} />
        </ListItem>
      ))}
    </List>
  );

  const sectionBlock = (section, imageSrc, index) => {
    const {
      title,
      intro,
      bullets,
      footer,
      imageOnLeft,
      showTrustBadgesOnImage,
    } = section;

    const imageColumn = (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: { xs: 256, md: 360 },
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          component='img'
          src={imageSrc}
          alt=''
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: { xs: 'center', md: 'center' },
            display: 'block',
          }}
        />
        {showTrustBadgesOnImage ? <TrustBadgesOnImage /> : null}
      </Box>
    );

    const textColumn = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, md: 2 },
          justifyContent: 'center',
          height: '100%',
          px: { xs: 0, md: 3, lg: 7.5 },
          py: { xs: 3, lg: 10 },
        }}
      >
        <Typography
          component='h2'
          sx={{
            fontWeight: 700,
            fontSize: { xs: 24, md: 28 },
            lineHeight: 1.2,
            letterSpacing: '-0.5px',
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        <Typography>{intro}</Typography>
        {renderChecklist(bullets)}
        <Typography>{footer}</Typography>
      </Box>
    );

    return (
      <Box
        key={`home-section-${index}`}
        sx={{
          display: 'flex',
          gap: { xs: 0, md: '12px', lg: 0 },
          flexDirection: {
            xs: 'column',
            lg: imageOnLeft ? 'row' : 'row-reverse',
          },
          alignItems: 'stretch',
          px: { xs: 3, md: '12px' },
          py: { xs: 2, md: 0, lg: 1.5 },
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          {imageColumn}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            backgroundColor: 'background.paper',
          }}
        >
          {textColumn}
        </Box>
      </Box>
    );
  };

  return (
    <Trackable loadId={ANALYTICS.HOME.LOAD}>
      <Box
        sx={{
          flexGrow: 1,
          position: 'relative',
        }}
      >
        {isMobile ? (
          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 2,
              backgroundColor: 'background.default',
            }}
          >
            <QuoteForm
              priceWithVatRateText={priceWithVatRateText}
              priceWithoutVatRateText={priceWithoutVatRateText}
            />
          </Box>
        ) : (
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Box
              sx={{
                width: '100%',
                pb: { md: 20 },
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: { md: 500 },
                  minHeight: { md: 500 },
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  px: 0,
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url(${heroBackgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  }}
                />
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(to right, rgba(0,0,0,0.8) 23.66%, rgba(0,0,0,0) 47.4%)',
                  }}
                />
                {heroContent}
              </Box>
            </Box>
            {sendParcelSection}
          </Box>
        )}
        <HomeUsps />
        {HOME_LANDING_SECTIONS.map((section, i) =>
          sectionBlock(section, SECTION_IMAGES[i], i)
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: { xs: 1.5, sm: 10 },
            py: { xs: 2, md: 2.5 },
            px: 2,
            mt: 3,
            backgroundColor: '#1A1A1A',
            color: 'primary.contrastText',
          }}
        >
          <TrustpilotHeroWordmark
            sx={{
              height: { xs: 20, md: 24 },
              width: { xs: 79, md: 94 },
            }}
          />
          <TrustpilotHeroStars
            sx={{
              height: { xs: 20, md: 24 },
              width: { xs: 103, md: 123 },
            }}
          />
          <Typography
            component='span'
            sx={{
              fontSize: { xs: 20, md: 24 },
              fontWeight: 500,
              lineHeight: 1,
              letterSpacing: 0.15,
            }}
          >
            {STRINGS.TRUSTPILOT_EXCELLENT}
          </Typography>
        </Box>
        <Fab
          variant='extended'
          color='primary'
          size='medium'
          component={Link}
          to={QUOTES}
          sx={{
            position: 'fixed',
            right: { xs: 16, md: 24 },
            bottom: { xs: 16, md: 24 },
            zIndex: theme => theme.zIndex.speedDial,
            textTransform: 'uppercase',
            fontWeight: 500,
            fontSize: { xs: 12, md: 14 },
            letterSpacing: 0.46,
            px: { xs: 1.5, md: 2 },
          }}
        >
          {STRINGS.SEND_NOW}
        </Fab>
      </Box>
    </Trackable>
  );
}

export default Home;
